/**
 * Financial Store — client-side selection state for the financial dashboard.
 *
 * Server state (data fetching) is managed by React Query hooks.
 * This store only holds UI selection state that needs to persist across
 * component re-renders without triggering network requests.
 */

import { create } from 'zustand';
import type { RegionConfig, UnifiedFinancialResponse } from '../types/financial';

type FinancialTab = 'revenue' | 'ebitda' | 'ev' | 'wacc' | 'roic' | 'sensitivity';

interface FinancialState {
  // Selected entity
  selectedCompanyName: string | null;
  selectedProjectId: string | null;

  // Year selection
  selectedYear: number | null;
  selectedYearsRange: 3 | 5 | 7 | 10;

  // Region config (set when a company response is received)
  regionConfig: RegionConfig | null;

  // Active tab in FinancialDashboardTab
  activeTab: FinancialTab;

  // Competitor comparison state — toggle on CompanyDetail page
  compareWithCompetitors: boolean;
  competitorsList: string[];
  competitorData: Record<string, UnifiedFinancialResponse>;
  competitorsLoading: boolean;
  competitorsError: string | null;
  // Peer fan-out progress (real counts, for the peer SmartLoader)
  competitorsLoaded: number;
  competitorsTotal: number;
  competitorsFailed: string[];

  // Actions
  setSelectedCompany: (name: string | null) => void;
  setSelectedProject: (id: string | null) => void;
  setSelectedYear: (year: number | null) => void;
  setYearsRange: (n: 3 | 5 | 7 | 10) => void;
  setRegionConfig: (config: RegionConfig | null) => void;
  setActiveTab: (tab: FinancialTab) => void;
  setCompareWithCompetitors: (b: boolean) => void;
  setCompetitorsList: (names: string[]) => void;
  upsertCompetitorData: (name: string, data: UnifiedFinancialResponse) => void;
  setCompetitorsLoading: (b: boolean) => void;
  setCompetitorsError: (err: string | null) => void;
  setCompetitorProgress: (p: { loaded: number; total: number; failed: string[] }) => void;
  resetCompetitorState: () => void;
  reset: () => void;
}

const initialCompetitorState = {
  compareWithCompetitors: false,
  competitorsList: [] as string[],
  competitorData: {} as Record<string, UnifiedFinancialResponse>,
  competitorsLoading: false,
  competitorsError: null as string | null,
  competitorsLoaded: 0,
  competitorsTotal: 0,
  competitorsFailed: [] as string[],
};

const initialState = {
  selectedCompanyName: null,
  selectedProjectId: null,
  selectedYear: null,
  selectedYearsRange: 5 as const,
  regionConfig: null,
  activeTab: 'revenue' as FinancialTab,
  ...initialCompetitorState,
};

export const useFinancialStore = create<FinancialState>((set) => ({
  ...initialState,

  setSelectedCompany: (name) => set({ selectedCompanyName: name }),
  setSelectedProject: (id) => set({ selectedProjectId: id }),
  setSelectedYear: (year) => set({ selectedYear: year }),
  setYearsRange: (n) => set({ selectedYearsRange: n }),
  setRegionConfig: (config) => set({ regionConfig: config }),
  setActiveTab: (tab) => set({ activeTab: tab }),
  setCompareWithCompetitors: (b) => set({ compareWithCompetitors: b }),
  setCompetitorsList: (names) =>
    set((s) => {
      // Prune competitorData to match the new selection. The peer-comparison
      // chart iterates competitorData directly, so any name left behind here
      // shows up as a stale extra bar after the user removes a competitor.
      const allowed = new Set(names.map((n) => n.toLowerCase()));
      const next: Record<string, UnifiedFinancialResponse> = {};
      for (const [name, data] of Object.entries(s.competitorData)) {
        if (allowed.has(name.toLowerCase())) next[name] = data;
      }
      return { competitorsList: names, competitorData: next };
    }),
  upsertCompetitorData: (name, data) =>
    set((s) => ({ competitorData: { ...s.competitorData, [name]: data } })),
  setCompetitorsLoading: (b) => set({ competitorsLoading: b }),
  setCompetitorsError: (err) => set({ competitorsError: err }),
  setCompetitorProgress: ({ loaded, total, failed }) =>
    set({ competitorsLoaded: loaded, competitorsTotal: total, competitorsFailed: failed }),
  resetCompetitorState: () => set({ ...initialCompetitorState }),
  reset: () => set(initialState),
}));
